import { useCallback, useRef, useEffect } from 'react';
import { Label, Input } from '@hqo/ui-atoms';
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Loader2 } from 'lucide-react';
import { cn } from '@hqo/react-hooks';
import { CreateVisitFormV2Values } from '../../../../types/form-data.types';
import { useVisitorTypeahead } from './hooks/use-visitor-typeahead.hook';
import { useValidationMessage } from '../../../../hooks/use-validation-message.hook';
import { VisitorDropdownItem } from './visitor-dropdown-item';

interface TypeaheadInputFieldProps {
  control: Control<CreateVisitFormV2Values>;
  setValue: UseFormSetValue<CreateVisitFormV2Values>;
  visitorIndex: number;
  required?: boolean;
  error?: { message?: string };
  disabled?: boolean;
}

export const TypeaheadInputField = ({
  control,
  setValue,
  visitorIndex,
  required,
  error,
  disabled = false,
}: TypeaheadInputFieldProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const formatValidationMessage = useValidationMessage();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fieldName = `visitors.${visitorIndex}.email` as const;

  const currentValue = useWatch({ control, name: fieldName as 'visitors.0.email' });

  const { visitorOptions, isLoading, isDropdownVisible, searchVisitor, selectVisitor, closeDropdown } =
    useVisitorTypeahead({
      setValue,
      visitorIndex,
    });

  const showError = Boolean(error);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeDropdown]);

  const label = formatMessage({ id: 'createVisitFormV2.visitStep.email', defaultMessage: "Visitor's email" });
  const placeholder = formatMessage({
    id: 'createVisitFormV2.visitStep.placeholders.email',
    defaultMessage: 'Enter email address',
  });
  const emptyResultsMessage = formatMessage({
    id: 'visitCreationForm.visitors.visitorSearchEmpty.email.title',
    defaultMessage: 'No visitors found with this email',
  });

  const handleChange = useCallback(
    (fieldOnChange: (value: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
      fieldOnChange(e.target.value);
      searchVisitor(e.target.value);
    },
    [searchVisitor],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape' || e.key === 'Tab') {
        closeDropdown();
      }
    },
    [closeDropdown],
  );

  return (
    <div className="space-y-2">
      <Label htmlFor={fieldName} className="text-sm font-medium text-foreground gap-1">
        {label}
        {required && <span className="text-destructive">*</span>}
      </Label>

      <div className="relative" ref={dropdownRef}>
        <Controller
          name={fieldName as 'visitors.0.email'}
          control={control}
          // eslint-disable-next-line react/jsx-no-bind
          render={({ field }) => (
            <div className="relative">
              <Input
                id={fieldName}
                type="email"
                placeholder={placeholder}
                className={cn(showError && 'border-destructive')}
                data-cy="email"
                disabled={disabled}
                {...field}
                onChange={handleChange(field.onChange)}
                onKeyDown={handleKeyDown}
              />
              {isLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                </div>
              )}
            </div>
          )}
        />

        {!disabled && isDropdownVisible && currentValue && currentValue.length >= 2 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {isLoading && (
              <div className="px-3 py-4 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            )}
            {!isLoading && visitorOptions.length > 0 && (
              <div className="py-1">
                {visitorOptions.map((visitor) => (
                  <VisitorDropdownItem
                    key={visitor.uuid}
                    visitor={visitor}
                    onSelect={selectVisitor}
                    subtitleField="email"
                  />
                ))}
              </div>
            )}
            {!isLoading && visitorOptions.length === 0 && (
              <div className="px-3 py-4 text-center">
                <p className="text-sm text-gray-500">{emptyResultsMessage}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {formatMessage({
                    id: 'visitCreationForm.visitors.visitorSearchEmpty.email.subtitle',
                    defaultMessage: 'Please try a different search or continue with the form',
                  })}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {showError && <p className="text-sm text-destructive">{formatValidationMessage(error?.message)}</p>}
    </div>
  );
};
